import styled from 'styled-components';
import { white, highlightGray, black } from 'styles/colors';
import {
  spacer8,
  spacer24,
  spacer32,
  spacer48,
  spacer12,
} from 'styles/spacers';
import { fontWeights } from 'styles/variables';
import { maxWidth, minWidth } from 'styles/media-queries';
import {
  gray70,
  IconEnhanceActive,
  ProgressDots,
} from '@spotify-internal/encore-web/core';
import Image from 'next/image';
import Link from 'next/link';
import { VOICE_IDS } from '../ai-voice-section/constants';
import { AUDIO_SCRIPT_PLAYBACK_CARD_COLORS } from './constants';

export const Container = styled.div<{
  variant: 'default' | 'highlight';
  selectedVoice?: typeof VOICE_IDS[keyof typeof VOICE_IDS];
}>`
  width: 100%;
  aspect-ratio: 1;
  background-color: ${({ variant, selectedVoice }) =>
    variant === 'highlight'
      ? AUDIO_SCRIPT_PLAYBACK_CARD_COLORS[selectedVoice || '']?.background
      : highlightGray};
  color: ${({ variant, selectedVoice }) =>
    variant === 'highlight'
      ? AUDIO_SCRIPT_PLAYBACK_CARD_COLORS[selectedVoice || '']?.text
      : gray70};
  border-radius: ${spacer8};
  padding: ${spacer32} ${spacer24};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  ${minWidth.md`
    width: 100%;
    height: 692px;
    aspect-ratio: unset;
  `}
`;

export const Content = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacer24};
  min-height: 0;
`;

export const TextContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: normal;
`;

export const Text = styled.p`
  text-align: center;
  font-size: 1.4rem;
  font-weight: ${fontWeights.extra_bold};

  ${minWidth.md`
   font-size: 2rem;
 `}
`;

export const Caption = styled.p`
  text-align: center;
  font-size: 1.4rem;
  font-weight: ${fontWeights.normal};
`;

export const EnhanceActiveIcon = styled(IconEnhanceActive)`
  transform: rotate(180deg);
`;

export const LoadingIndicator = styled(ProgressDots)<{
  selectedVoice: typeof VOICE_IDS[keyof typeof VOICE_IDS];
}>`
  circle {
    fill: ${({ selectedVoice }) =>
      AUDIO_SCRIPT_PLAYBACK_CARD_COLORS[selectedVoice || '']?.text};
  }
`;

export const GeneratedAt = styled.span<{
  selectedVoice: typeof VOICE_IDS[keyof typeof VOICE_IDS];
}>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${spacer8};
  font-size: 1.4rem;
  font-weight: ${fontWeights.normal_p};
  color: ${({ selectedVoice }) =>
    AUDIO_SCRIPT_PLAYBACK_CARD_COLORS[selectedVoice || '']?.highlightText};
  flex: 0 0 auto;
`;

export const PlayButton = styled.button<{
  selectedVoice: typeof VOICE_IDS[keyof typeof VOICE_IDS];
}>`
  width: 5.6rem;
  height: 5.6rem;
  border-radius: 100%;
  border: none;
  background-color: ${({ selectedVoice }) =>
    AUDIO_SCRIPT_PLAYBACK_CARD_COLORS[selectedVoice || '']?.playButton};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ selectedVoice }) =>
    AUDIO_SCRIPT_PLAYBACK_CARD_COLORS[selectedVoice || '']?.playButtonText};
  flex: 0 0 auto;
`;

export const ScriptContainer = styled.div<{
  selectedVoice: typeof VOICE_IDS[keyof typeof VOICE_IDS];
}>`
  flex: 1;
  height: 100%;
  margin: ${spacer8} 0;
  overflow-y: auto;

  -ms-overflow-style: none; /* for Internet Explorer, Edge */
  scrollbar-width: none; /* for Firefox */

  &::-webkit-scrollbar {
    display: none;
  }

  ${minWidth.md`
    margin: ${spacer8} ${spacer48};
  `}

  &:after {
    position: absolute;
    bottom: 0;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    content: '';
    pointer-events: none;
    background: linear-gradient(
      to bottom,
      ${({ selectedVoice }) =>
          AUDIO_SCRIPT_PLAYBACK_CARD_COLORS[selectedVoice || '']?.background}
        0%,
      transparent 10%,
      transparent 90%,
      ${({ selectedVoice }) =>
          AUDIO_SCRIPT_PLAYBACK_CARD_COLORS[selectedVoice || '']?.background}
        100%
    );
  }
`;

export const Script = styled.p`
  width: 100%;
  font-size: 2.6rem;
  line-height: 110%;
  font-weight: ${fontWeights.extra_bold};
  margin-top: ${spacer24};
  margin-bottom: ${spacer24};

  ${minWidth.md`
   font-size: 4rem;
   margin-top: ${spacer48};
   margin-bottom: ${spacer48};
 `}
`;

export const Carousel = styled.div`
  position: relative;
  width: 80%;
  height: 60%;

  ${maxWidth.xs`
    display: none;
  `}
`;

export const CTAImage = styled(Image)<{
  isActive: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  opacity: ${({ isActive }) => (isActive ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;
`;

export const CTACaption = styled.p`
  text-align: center;
  font-size: 2rem;
  font-weight: ${fontWeights.normal_p};

  ${minWidth.md`
    font-size: 2.4rem;
  `}
`;

export const CTAButton = styled(Link)`
  padding: ${spacer12} ${spacer24};
  border-radius: 5rem;
  border: solid ${white} 0.1rem;
  font-size: 1.6rem;
  font-weight: ${fontWeights.extra_bold};
  background-color: ${white};
  color: ${black};
  line-height: 1.76rem;
`;

export const ListenAgainButton = styled.button<{
  selectedVoice: typeof VOICE_IDS[keyof typeof VOICE_IDS];
}>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacer8};
  font-size: 1.4rem;
  font-weight: ${fontWeights.bold};
  flex: 0 0 auto;
  color: ${({ selectedVoice }) =>
    AUDIO_SCRIPT_PLAYBACK_CARD_COLORS[selectedVoice || '']?.text};
`;
